import axios from "axios";
import {refreshAccessToken} from "../services/authService";
import {getApiErrorMessage} from "../utils/apiError";
import {clearTokens, getAccessToken, getRefreshToken, setTokens} from "../utils/identityTokens";

const APIAUTH_URL = `${process.env.REACT_APP_API_AUTH}/admin`;

// Dedupe de refresh: si varias requests reciben 401 a la vez, solo se renueva una.
let refreshPromise = null;

// Evento que avisa al IdentityAuthContext de que los tokens han cambiado
export const TOKENS_UPDATED_EVENT = "identity:tokens-updated";

const apiClient = axios.create({
  baseURL: APIAUTH_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

/** Logout duro: limpia tokens internos y vuelve a la pantalla de acceso. */
const hardLogout = () => {
  clearTokens();
  if (window.location.pathname !== "/acceso") {
    window.location.replace("/acceso");
  }
};

// ==============================
// 🟢 REQUEST INTERCEPTOR
// ==============================
// Inyecta el access_token de Identity (storage) como Bearer en cada request.
apiClient.interceptors.request.use(
  (config) => {
    if (!config.headers) {
      config.headers = {};
    }

    if (config.headers.Authorization) {
      return config;
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ==============================
// 🔄 RESPONSE INTERCEPTOR
// ==============================
// Ante un 401, renueva el access_token con el refresh_token y reintenta.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    error.userMessage = getApiErrorMessage(error);

    // No es 401, no hay request, o ya se reintentó → propaga.
    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      hardLogout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken(refreshToken)
          .then((tokens) => {
            setTokens(tokens);
            window.dispatchEvent(new Event(TOKENS_UPDATED_EVENT));
            return tokens;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      const tokens = await refreshPromise;

      if (!tokens?.access_token) {
        throw new Error("No access token after refresh");
      }

      // Reinyecta el nuevo token y reintenta la request original.
      originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      refreshError.userMessage = getApiErrorMessage(refreshError);
      hardLogout();
      return Promise.reject(refreshError);
    }
  }
);

export default apiClient;
